import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { POST_QUERY_KEY } from './useGetPost';

export type ApiResponse_Toggle_Post_Like = {
  id: number;
  liked: boolean;
  likeCount: number;
};

const METHOD = 'POST';
const ENDPOINT = 'posts/{postId}/like';

export const useTogglePostLike = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { postId: number; communityId?: number; groupId?: number }) => {
      try {
        const { postId } = params;
        const data = await handleApiCall<ApiResponse_Toggle_Post_Like, Record<string, never>>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            postId: postId.toString(),
          }),
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to toggle like.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific post to update like counts and status
      queryClient.invalidateQueries({
        queryKey: POST_QUERY_KEY(data.id),
      });
      
      // Invalidate community posts if specified
      if (variables.communityId) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/community/${variables.communityId}`);
            }
            return false;
          },
        });
      }
      
      // Invalidate group posts if specified
      if (variables.groupId) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as unknown[];
            const firstKey = queryKey[0];
            if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
              return firstKey[1].includes(`posts/group/${variables.groupId}`);
            }
            return false;
          },
        });
      }
    },
  });
};